'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import first from 'lodash/first';
import slice from 'lodash/slice';
import styles from './sectionsContainer.module.scss';
import SectionPanel from './sectionPanel';
import { getTopicIconByID, navigateToAnchor } from '@/utils/helpCenterUtils';
import { useRouter } from 'next/navigation';
import OHCContext from '@/providers/OHCContext';
import { KibContainer } from '@chewy/kib-layout-react';
import '@chewy/kib-layout-styles/dist/styles/kib-container.css';

export interface SectionsContainerProps {
  readonly sectionsData: any[] | undefined | null;
  readonly jumpID: any | undefined | null;
  readonly showViewAll?: boolean;
}

export default function SectionsContainer({ sectionsData = [], jumpID, showViewAll = false }: SectionsContainerProps) {
  const jumpRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { maxCategoriesShown, categoryIdsMap } = React.useContext(OHCContext);
  const shouldJump = useMemo(() => jumpID !== `/${first(sectionsData)?.id}`, [jumpID, sectionsData]);

  useEffect(() => {
    if (jumpID && jumpRef.current && shouldJump) {
      jumpRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [jumpID, jumpRef, shouldJump]);

  const handleAnchorClick = (topicData: any) => {
    if (isEmpty(topicData?.sections)) {
      return;
    }
    // If the topic has no sections, we can navigate to the first section's anchor
    const section = first(topicData.sections) as any;
    if (!section?.id) {
      return;
    }
    navigateToAnchor(section?.id, router);
  };

  return (
    <div className={styles.wrapper}>
      {map(sectionsData, (sectionData: any) => {
        const isJumpSection = jumpID && jumpID === `/${sectionData.id}`;
        console.log('Rendering section:', sectionData?.id, 'isJumpSection:', isJumpSection);
        const sectionIcons = getTopicIconByID(sectionData?.categoryId, categoryIdsMap);
        return (
          <div data-testid={`section-panel-${sectionData?.id}`} key={sectionData?.id}>
            <KibContainer data-testid={`section-${sectionData?.id}`} className={styles.section} id={sectionData?.id} ref={isJumpSection ? jumpRef : null}>
              {sectionData?.id && (
                <span
                  data-testid={`anchor-${sectionData.id}`}
                  onClick={() => navigateToAnchor(sectionData.id, router)}
                  id={`anchor-${sectionData.id}`}
                  className={styles.anchorLink}
                ></span>
              )}

              <div data-testid={`section-header-${sectionData?.id}`} className={styles.sectionHeader}>
                {sectionIcons?.start && (
                  <span data-testid={`section-icon-start-${sectionData?.id}`} className={styles.sectionHeaderIcon}>
                    {sectionIcons?.start}
                  </span>
                )}
                <h2 data-testid={`section-title-${sectionData?.id}`} className={styles.sectionTitle}>
                  {sectionData?.title}
                </h2>
                {sectionIcons?.end && (
                  <span data-testid={`section-icon-end-${sectionData?.id}`} className={styles.sectionIllustration}>
                    {sectionIcons.end}
                  </span>
                )}
              </div>
              {!isEmpty(sectionData?.sections) && (
                <ul data-testid={`section-links-${sectionData?.id}`} className={styles.linkList}>
                  {map(slice(sectionData?.sections, 0, showViewAll ? maxCategoriesShown : sectionData?.sections?.length), (section: any) => (
                    <SectionPanel key={section?.id} section={section} />
                  ))}
                </ul>
              )}

              {!isEmpty(sectionData?.sections) && sectionData?.sections?.length > maxCategoriesShown && showViewAll && (
                <span data-testid={`view-all-${sectionData?.id}`} onClick={() => handleAnchorClick(sectionData)} className={styles.viewAllLink}>
                  {'View all articles'} &rarr;
                </span>
              )}
            </KibContainer>
            </div>
        );
      })}
    </div>
  );
}
